import React, { createContext, useContext } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useObjectTabs } from '../data/objectTabs';
import { envKind, useCockpit } from '../data/CockpitContext';
import { EnvChip, Spinner } from '../components/ui';
import { ExactLimitsHeader } from '../components/ExactLimitsHeader';
import { v2 } from '../paths';

const InEnv = createContext(false);

/** True inside the frame below: the page leaves the heading and the tabs to it. */
export const useInEnvFrame = () => useContext(InEnv);

const tabClass = ({ isActive }: { isActive: boolean }) => (isActive ? 'tab on' : 'tab');

/**
 * The frame of every tab of one environment: the same heading and the same tabs, so moving
 * from Live to Exact never feels like leaving the environment.
 */
export const EnvLayout = () => {
    const { customer = '', environment = '' } = useParams();
    const { find, isAdmin } = useCockpit();
    const env = find(customer, environment);
    const kind = env ? envKind(env) : environment.toLowerCase().startsWith('prod') ? 'prod' : 'test';
    const { tabs, close } = useObjectTabs(customer, environment);
    const location = useLocation();
    const navigate = useNavigate();

    return (
        <InEnv.Provider value={true}>
            <div className="pagehead">
                <div>
                    <div className="eyebrow">Klant · omgeving</div>
                    <h1 className="h1">
                        {customer} <EnvChip kind={kind} environment={environment} />
                    </h1>
                </div>
                {isAdmin && env && (
                    <div className="hrow" style={{ gap: 16 }}>
                        <ExactLimitsHeader customer={customer} environment={environment} />
                        {env.watchStatus === 'watching' && (
                            <span className="chip info">
                                <Spinner />
                                Monitor loopt
                            </span>
                        )}
                    </div>
                )}
            </div>

            {isAdmin && (
                <nav className="tabs" aria-label={`Onderdelen van ${customer} ${environment}`}>
                    <NavLink end className={tabClass} to={v2.env(customer, environment)}>
                        Live
                    </NavLink>
                    <NavLink className={tabClass} to={v2.envIssues(customer, environment)}>
                        Issues <span className="cnt">{env ? env.open ?? 0 : '…'}</span>
                    </NavLink>
                    <NavLink className={tabClass} to={v2.errorLog(customer, environment)}>
                        Foutenlogboek
                    </NavLink>
                    <NavLink className={tabClass} to={v2.monitor(customer, environment)}>
                        Monitor {env?.watchStatus === 'watching' && <span className="cnt amb">live</span>}
                    </NavLink>
                    <NavLink className={tabClass} to={v2.envExact(customer, environment)}>
                        Exact
                    </NavLink>
                    {tabs.length > 0 && <span className="tabsep" aria-hidden="true" />}
                    {tabs.map((t) => {
                        const path = v2.object(customer, environment, t.id);
                        const active = decodeURIComponent(location.pathname).toLowerCase() === decodeURIComponent(path).toLowerCase();
                        return (
                            <span key={t.id} className={`tab objtab ${active ? 'on' : ''}`}>
                                <Link to={path} title={t.label}>
                                    <span className="mono">{t.label}</span>
                                </Link>
                                <button
                                    className="x"
                                    aria-label={`${t.label} sluiten`}
                                    onClick={() => {
                                        close(t.id);
                                        if (active) navigate(v2.env(customer, environment));
                                    }}
                                >
                                    ×
                                </button>
                            </span>
                        );
                    })}
                </nav>
            )}

            <Outlet />
        </InEnv.Provider>
    );
};
